import * as React from "react";

import type { LifecycleStateLabel, ProjectRun } from "@/api/projects";
import { RunStateBadge } from "@/components/runs/run-state-badge";

/**
 * AG-Grid cell renderer set for the FlowStack runs table.
 *
 * Each renderer is a thin React component AG-Grid mounts via the
 * ``cellRendererFramework`` API. Keep them pure — no async fetches,
 * no animations that fight AG-Grid's row virtualization.
 */

interface CellParams<T = ProjectRun, V = unknown> {
  value: V;
  data: T;
}

/** State badge — wraps the signature RunStateBadge.  */
export function RunStateCell({ value }: CellParams<ProjectRun, LifecycleStateLabel>): JSX.Element {
  return (
    <div className="flex h-full items-center">
      <RunStateBadge state={value} />
    </div>
  );
}

/** Cost cell — right-aligned, mono tabular nums, currency subscript. */
export function CostCell({
  value,
}: CellParams<ProjectRun, number | null | undefined>): JSX.Element {
  if (value === null || value === undefined) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <span className="flex h-full items-center justify-end gap-0.5 font-mono tabular-nums">
      <span className="text-foreground">{value.toFixed(2)}</span>
      <span className="text-xs text-muted-foreground">USD</span>
    </span>
  );
}

/** Relative-time cell — "2m ago" with full timestamp on hover.  */
export function RelativeTimeCell({
  value,
}: CellParams<ProjectRun, string | null | undefined>): JSX.Element {
  const [, force] = React.useReducer((x: number) => x + 1, 0);

  // Re-render every 30s so "2m ago" stays current. AG-Grid virtualizes
  // rows; off-screen cells unmount the timer for free.
  React.useEffect(() => {
    const id = window.setInterval(force, 30_000);
    return () => window.clearInterval(id);
  }, []);

  if (!value) return <span className="text-muted-foreground">—</span>;
  const rel = relativeTime(new Date(value));
  return (
    <time
      dateTime={value}
      title={new Date(value).toLocaleString()}
      className="text-xs text-muted-foreground"
    >
      {rel}
    </time>
  );
}

/** Run ID cell — mono, truncated; full id on hover. */
export function RunIdCell({ value }: CellParams<ProjectRun, string>): JSX.Element {
  return (
    <span title={value} className="font-mono text-xs tabular-nums text-foreground">
      {value.length > 18 ? `${value.slice(0, 16)}…` : value}
    </span>
  );
}

/** Workload cell — flow_name OR tile_name, fall back to run_id. */
export function WorkloadCell({ data }: CellParams<ProjectRun>): JSX.Element {
  const label = data.flow_name ?? data.tile_name ?? data.run_id;
  const subLabel = data.flow_name ? "flow" : data.tile_name ? "tile" : "run";
  return (
    <div className="flex h-full flex-col justify-center">
      <span className="truncate text-sm font-medium text-foreground">{label}</span>
      <span className="label-micro text-muted-foreground">{subLabel}</span>
    </div>
  );
}

/** Progress cell — determinate bar for actively-running rows; ``—`` otherwise. */
export function ProgressCell({ data }: CellParams<ProjectRun>): JSX.Element {
  const active = ACTIVE_STATES.has(data.status);
  if (!active) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="flex h-full items-center" aria-label="In progress">
      <div className="h-1 w-full overflow-hidden rounded-full bg-state-running-bg">
        <div className="h-full w-1/2 bg-state-running motion-safe:animate-pulse" />
      </div>
    </div>
  );
}

const ACTIVE_STATES = new Set<LifecycleStateLabel>([
  "validating",
  "cached",
  "staging_inputs",
  "dispatched",
  "running",
  "collecting_outputs",
  "validating_outputs",
]);

/** Exported for reuse outside AG-Grid cell renderers (e.g. the Analysis
 * project-runs list, flowstack VIZ U4.1) — the same "2m ago" formatting
 * the tenant-wide/project-scoped run tables already use. */
export function relativeTime(then: Date): string {
  const seconds = Math.max(0, Math.floor((Date.now() - then.getTime()) / 1000));
  if (seconds < 5) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return then.toLocaleDateString();
}
